import {api} from '../../services/api';
import {
    AiTradeMode,
    RiskMode,
    TradeType,
    TradingStyle,
    UpdateConfigRequest,
    UserAiTradeConfig,
    VALID_FREQUENCIES
} from './types';

interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

export const userAiConfigService = {
    /**
     * 获取当前用户的AI交易配置
     */
    getConfig: async (): Promise<UserAiTradeConfig> => {
        const response = await api.get<ApiResponse<UserAiTradeConfig>>('/api/v1/users/ai-config');
        if (response.data && response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error('获取配置失败');
    },

    /**
     * 更新AI交易模式
     */
    updateAiTradeMode: async (mode: AiTradeMode): Promise<UserAiTradeConfig> => {
        const response = await api.put<ApiResponse<UserAiTradeConfig>>(
            `/api/v1/users/ai-config/ai-trade-mode?mode=${mode}`
        );
        if (response.data && response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error('更新AI交易模式失败');
    },

    /**
     * 更新AI交易频次
     */
    updateAiTradeFrequency: async (frequency: number): Promise<UserAiTradeConfig> => {
        if (!VALID_FREQUENCIES.includes(frequency)) {
            throw new Error(`无效的频次值: ${frequency}`);
        }
        const response = await api.put<ApiResponse<UserAiTradeConfig>>(
            `/api/v1/users/ai-config/ai-trade-frequency?frequency=${frequency}`
        );
        if (response.data && response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error('更新AI交易频次失败');
    },

    /**
     * 更新风控模式
     */
    updateRiskMode: async (mode: RiskMode): Promise<UserAiTradeConfig> => {
        const response = await api.put<ApiResponse<UserAiTradeConfig>>(
            `/api/v1/users/ai-config/risk-mode?mode=${mode}`
        );
        if (response.data && response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error('更新风控模式失败');
    },

    /**
     * 更新交易风格
     */
    updateTradingStyle: async (style: TradingStyle): Promise<UserAiTradeConfig> => {
        const response = await api.put<ApiResponse<UserAiTradeConfig>>(
            `/api/v1/users/ai-config/trading-style?style=${style}`
        );
        if (response.data && response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error('更新交易风格失败');
    },

    /**
     * 更新交易类型
     */
    updateTradeType: async (tradeType: TradeType): Promise<UserAiTradeConfig> => {
        const response = await api.put<ApiResponse<UserAiTradeConfig>>(
            `/api/v1/users/ai-config/trade-type?tradeType=${tradeType}`
        );
        if (response.data && response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error('更新交易类型失败');
    },

    /**
     * 更新最大并行资金
     * @param maxParallelCapital 最大并行资金（USDT），null表示使用系统默认值
     */
    updateMaxParallelCapital: async (maxParallelCapital: number | null): Promise<UserAiTradeConfig> => {
        const url = maxParallelCapital != null
            ? `/api/v1/users/ai-config/max-parallel-capital?maxParallelCapital=${maxParallelCapital}`
            : '/api/v1/users/ai-config/max-parallel-capital';
        const response = await api.put<ApiResponse<UserAiTradeConfig>>(url);
        if (response.data && response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error('更新最大并行资金失败');
    },

    /**
     * 批量更新配置
     */
    updateConfig: async (request: UpdateConfigRequest): Promise<UserAiTradeConfig> => {
        const response = await api.put<ApiResponse<UserAiTradeConfig>>('/api/v1/users/ai-config', request);
        if (response.data && response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error('更新配置失败');
    },

    /**
     * 更新绑定的交易Agent
     */
    updateAgentConfigId: async (agentConfigId: number | null): Promise<UserAiTradeConfig> => {
        const url = agentConfigId != null
            ? `/api/v1/users/ai-config/agent-config-id?agentConfigId=${agentConfigId}`
            : '/api/v1/users/ai-config/agent-config-id';
        const response = await api.put<ApiResponse<UserAiTradeConfig>>(url);
        if (response.data && response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error('更新交易Agent失败');
    },

    /**
     * 获取有效的频次选项
     */
    getValidFrequencies: async (): Promise<number[]> => {
        const response = await api.get<ApiResponse<number[]>>('/api/v1/users/ai-config/valid-frequencies');
        if (response.data && response.data.success && response.data.data) {
            return response.data.data;
        }
        return VALID_FREQUENCIES;
    }
};
